import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import {
  GET_ACCOUNT_DEAL_PREFERENCES_AND_PREFERENCES,
  type InvestmentPreferencesData
} from '../queries/accountPreferencesQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof InvestmentPreferencesData['targetCompanyPreferences'];
}

const DEAL_PREFERENCES_FIELDS: FieldMapping[] = [
  { apiField: 'minRevenue', formField: 'minAnnualRevenue' },
  { apiField: 'minRevenueBase', formField: 'minAnnualRevenueBase' },
  { apiField: 'maxRevenue', formField: 'maxAnnualRevenue' },
  { apiField: 'maxRevenueBase', formField: 'maxAnnualRevenueBase' },
  { apiField: 'minEbitda', formField: 'minEbitda' },
  { apiField: 'minEbitdaBase', formField: 'minEbitdaBase' },
  { apiField: 'maxEbitda', formField: 'maxEbitda' },
  { apiField: 'maxEbitdaBase', formField: 'maxEbitdaBase' },
  { apiField: 'minInvestmentAmount', formField: 'minInvestmentAmount' },
  { apiField: 'minInvestmentAmountBase', formField: 'minInvestmentAmountBase' },
  { apiField: 'maxInvestmentAmount', formField: 'maxInvestmentAmount' },
  { apiField: 'maxInvestmentAmountBase', formField: 'maxInvestmentAmountBase' },
  { apiField: 'minDealSize', formField: 'minDealSize' },
  { apiField: 'minDealSizeBase', formField: 'minDealSizeBase' },
  { apiField: 'maxDealSize', formField: 'maxDealSize' },
  { apiField: 'maxDealSizeBase', formField: 'maxDealSizeBase' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: InvestmentPreferencesData['targetCompanyPreferences'],
  initial: InvestmentPreferencesData['targetCompanyPreferences']
): Record<string, null | number> {
  const changes: Record<string, null | number> = {};

  for (const { apiField, formField } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    if (currentVal !== initialVal) {
      changes[apiField] = parseCurrencyToNumber(currentVal);
    }
  }

  return changes;
}

// Parse currency string to number (e.g., "$1,000" -> 1000)
function parseCurrencyToNumber(value: string): null | number {
  if (!value || value === '-') return null;
  const cleaned = value.replace(/[$,]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

const UPSERT_DEAL_PREFERENCES = gql`
  mutation UpsertDealPreferences(
    $accountId: ID!
    $values: DealPreferencesUpsertInput!
  ) {
    upsertDealPreferences(accountId: $accountId, values: $values) {
      ok
      created
      dealPreferences {
        id
        partnerId {
          id
          name
        }
        currencyId
        minRevenue
        minRevenueBase
        maxRevenue
        maxRevenueBase
        minEbitda
        minEbitdaBase
        maxEbitda
        maxEbitdaBase
        minInvestmentAmount
        minInvestmentAmountBase
        maxInvestmentAmount
        maxInvestmentAmountBase
        minDealSize
        minDealSizeBase
        maxDealSize
        maxDealSizeBase
      }
    }
  }
`;

interface EditTargetCompanyPreferencesDialogProps {
  accountId: string;
  initialValue: InvestmentPreferencesData;
  isOpen: boolean;
  onChange: (data: InvestmentPreferencesData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: InvestmentPreferencesData;
}

export function EditTargetCompanyPreferencesDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditTargetCompanyPreferencesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [upsertDealPreferences] = useMutation(UPSERT_DEAL_PREFERENCES, {
    refetchQueries: [
      {
        query: GET_ACCOUNT_DEAL_PREFERENCES_AND_PREFERENCES,
        variables: { accountId }
      }
    ]
  });

  const changes = getChangedValues(
    DEAL_PREFERENCES_FIELDS,
    value.targetCompanyPreferences,
    initialValue.targetCompanyPreferences
  );
  const isDirty = Object.keys(changes).length > 0;

  const updateField = (
    field: keyof typeof value.targetCompanyPreferences,
    fieldValue: string
  ) => {
    onChange({
      ...value,
      targetCompanyPreferences: {
        ...value.targetCompanyPreferences,
        [field]: fieldValue
      }
    });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertDealPreferences({
        variables: { accountId, values: changes }
      });

      const data = response.data as {
        upsertDealPreferences?: { ok?: boolean };
      };

      if (data.upsertDealPreferences?.ok === false) {
        toast.error('Failed to save deal preferences');
      } else {
        toast.success('Deal preferences saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving deal preferences:', error);
      toast.error('Failed to save deal preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Target Company and Deal Preferences ($mm)"
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        {/* Annual Revenue */}
        <EditFormField
          label="(Min) Annual Revenue"
          onChange={v => {
            updateField('minAnnualRevenue', v);
          }}
          placeholder="Enter minimum annual revenue"
          value={value.targetCompanyPreferences.minAnnualRevenue}
        />
        <EditFormField
          label="(Min) Annual Revenue (Base)"
          onChange={v => {
            updateField('minAnnualRevenueBase', v);
          }}
          placeholder="Enter minimum annual revenue base"
          value={value.targetCompanyPreferences.minAnnualRevenueBase}
        />
        <EditFormField
          label="(Max) Annual Revenue"
          onChange={v => {
            updateField('maxAnnualRevenue', v);
          }}
          placeholder="Enter maximum annual revenue"
          value={value.targetCompanyPreferences.maxAnnualRevenue}
        />
        <EditFormField
          label="(Max) Annual Revenue (Base)"
          onChange={v => {
            updateField('maxAnnualRevenueBase', v);
          }}
          placeholder="Enter maximum annual revenue base"
          value={value.targetCompanyPreferences.maxAnnualRevenueBase}
        />

        {/* EBITDA */}
        <EditFormField
          label="(Min) EBITDA"
          onChange={v => {
            updateField('minEbitda', v);
          }}
          placeholder="Enter minimum EBITDA"
          value={value.targetCompanyPreferences.minEbitda}
        />
        <EditFormField
          label="(Min) EBITDA (Base)"
          onChange={v => {
            updateField('minEbitdaBase', v);
          }}
          placeholder="Enter minimum EBITDA base"
          value={value.targetCompanyPreferences.minEbitdaBase}
        />
        <EditFormField
          label="(Max) EBITDA"
          onChange={v => {
            updateField('maxEbitda', v);
          }}
          placeholder="Enter maximum EBITDA"
          value={value.targetCompanyPreferences.maxEbitda}
        />
        <EditFormField
          label="(Max) EBITDA (Base)"
          onChange={v => {
            updateField('maxEbitdaBase', v);
          }}
          placeholder="Enter maximum EBITDA base"
          value={value.targetCompanyPreferences.maxEbitdaBase}
        />

        {/* Investment Amount */}
        <EditFormField
          label="(Min) Investment Amount"
          onChange={v => {
            updateField('minInvestmentAmount', v);
          }}
          placeholder="Enter minimum investment amount"
          value={value.targetCompanyPreferences.minInvestmentAmount}
        />
        <EditFormField
          label="(Min) Investment Amount (Base)"
          onChange={v => {
            updateField('minInvestmentAmountBase', v);
          }}
          placeholder="Enter minimum investment amount base"
          value={value.targetCompanyPreferences.minInvestmentAmountBase}
        />
        <EditFormField
          label="(Max) Investment Amount"
          onChange={v => {
            updateField('maxInvestmentAmount', v);
          }}
          placeholder="Enter maximum investment amount"
          value={value.targetCompanyPreferences.maxInvestmentAmount}
        />
        <EditFormField
          label="(Max) Investment Amount (Base)"
          onChange={v => {
            updateField('maxInvestmentAmountBase', v);
          }}
          placeholder="Enter maximum investment amount base"
          value={value.targetCompanyPreferences.maxInvestmentAmountBase}
        />

        {/* Deal Size */}
        <EditFormField
          label="(Min) Deal Size"
          onChange={v => {
            updateField('minDealSize', v);
          }}
          placeholder="Enter minimum deal size"
          value={value.targetCompanyPreferences.minDealSize}
        />
        <EditFormField
          label="(Min) Deal Size (Base)"
          onChange={v => {
            updateField('minDealSizeBase', v);
          }}
          placeholder="Enter minimum deal size base"
          value={value.targetCompanyPreferences.minDealSizeBase}
        />
        <EditFormField
          label="(Max) Deal Size"
          onChange={v => {
            updateField('maxDealSize', v);
          }}
          placeholder="Enter maximum deal size"
          value={value.targetCompanyPreferences.maxDealSize}
        />
        <EditFormField
          label="(Max) Deal Size (Base)"
          onChange={v => {
            updateField('maxDealSizeBase', v);
          }}
          placeholder="Enter maximum deal size base"
          value={value.targetCompanyPreferences.maxDealSizeBase}
        />
      </div>
    </EditDialog>
  );
}
